import { parseURITemplate } from '@superdispatch/uri';
import { CTMS_HOST } from 'shared/constants/ServerConstants';
import { UTMSource } from 'shared/helpers/Analytics';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { joinStrings } from 'shared/utils/StringUtils';
import {
  yupBoolean,
  yupDateString,
  yupEnum,
  yupNumber,
  yupObject,
  yupString,
} from 'shared/utils/YupUtils';
import { InferType, number, string } from 'yup';
import { PostingLoadDTO, postingLoadSchema } from './PostingLoadDTO';

export type PostingLoadEditsVenueDTO = InferType<
  typeof postingLoadEditsVenueSchema
>;
const postingLoadEditsVenueSchema = yupObject({
  city: string().nullable().default(null),
  state: string().nullable().default(null),
  zip: string().nullable().default(null).uppercase(),
});

export type PostingLoadEditsDTO = InferType<typeof postingLoadEditsSchema>;
const postingLoadEditsSchema = yupObject({
  previous_price: yupNumber(),
  previous_pickup_venue: postingLoadEditsVenueSchema.nullable().default(null),
  previous_delivery_venue: postingLoadEditsVenueSchema.nullable().default(null),
});

export type PostingCarrierLoadDTO = InferType<typeof postingCarrierLoadSchema>;
export const postingCarrierLoadSchema = yupObject({
  guid: yupString(),
  load_guid: yupString().nullable(),
  posting_guid: yupString().nullable(),
  carrier_guid: yupString().nullable(),
  carrier_name: yupString().nullable(),
  carrier_contact_name: yupString().nullable(),
  carrier_comment: yupString().nullable(),
  price: yupNumber().required(),
  counter_offer_price: yupNumber(),
  created_at: yupDateString('JodaISO'),
  changed_at: yupDateString('JodaISO'),
  delivery_date: yupDateString('JodaISO'),
  pickup_date: yupDateString('JodaISO'),
  status: yupEnum([
    'REQUEST_PENDING',
    'REQUEST_DECLINED_BY_BROKER',
    'REQUEST_CANCELED',
    'OFFER_SENT',
    'OFFER_ACCEPTED',
    'OFFER_CANCELED',
    'OFFER_DECLINED',
    'COUNTER_OFFER_SENT',
    'UNPOSTED',
  ]),
  carrier_load_type: yupEnum(['REQUEST', 'BOOKING']),
  broker_offer_guid: yupString().nullable(),
  carrier_order_guid: yupString().nullable(),
  broker_decline_reason: yupString().nullable(),
  load_edited_at: yupDateString('JodaISO').nullable(),
  load_edits: postingLoadEditsSchema.nullable().default(null),
});

export function getViewOfferLink(
  { broker_offer_guid: brokerOfferGuid }: PostingCarrierLoadDTO,
  source: 'Load Details' | 'Load List',
) {
  return brokerOfferGuid == null
    ? undefined
    : MobileAppBridge.isInjected()
      ? parseURITemplate(
          `${CTMS_HOST}/tms/public-offer/{guid}{?utm_medium,utm_source}`,
          {
            guid: brokerOfferGuid,
            utm_medium: source,
            utm_source: 'Mobile SLB' satisfies UTMSource,
          },
        )
      : parseURITemplate('/tms/offers/{guid}{?utm_medium,utm_source}', {
          guid: brokerOfferGuid,
          utm_medium: source,
          utm_source: 'Web SLB' satisfies UTMSource,
        });
}

export function getOrderLink(
  carrierLogInfo: PostingCarrierLoadDTO,
  source: 'Load Details' | 'Load List',
) {
  return parseURITemplate('/tms/loads/{guid}{?utm_medium,utm_source}', {
    utm_medium: source,
    guid: carrierLogInfo.carrier_order_guid,
    utm_source: (MobileAppBridge.isInjected()
      ? 'Mobile SLB'
      : 'Web SLB') satisfies UTMSource,
  });
}

const postingDistanceSchema = yupObject({
  distance_from: yupString().nullable(),
  distance_miles: yupNumber().nullable(),
  direction: yupString().nullable(),
});
export type PostingDistanceDTO = InferType<typeof postingDistanceSchema>;

export type PostingDTO = InferType<typeof postingSchema>;
export const postingSchema = yupObject({
  suggestion_id: number().nullable(),
  is_suggestion_seen: yupBoolean(),
  load: postingLoadSchema,
  carrier_load: postingCarrierLoadSchema.nullable(),
  pickup_distance: postingDistanceSchema.nullable(),
  delivery_distance: postingDistanceSchema.nullable(),
  is_saved: yupBoolean(),
  is_shipper_recommended: yupBoolean().optional(),
});

export function getPostingState(
  load: PostingLoadDTO,
  carrierPostingInfo?: null | PostingCarrierLoadDTO,
):
  | 'can_book'
  | 'can_send_request'
  | 'can_update_request'
  | 'offer_accepted'
  | 'received_offer'
  | 'unposted' {
  const isUnposted = load.unposted_at;
  const carrierPostingStatus = carrierPostingInfo?.status;

  // Filter out already sent requests.
  if (carrierPostingStatus === 'REQUEST_PENDING') {
    return 'can_update_request';
  }

  // Filter out received offers.
  if (carrierPostingStatus === 'OFFER_SENT') {
    return 'received_offer';
  }

  // Filter out accepted loads.
  if (carrierPostingStatus === 'OFFER_ACCEPTED') {
    return 'offer_accepted';
  }

  // Filter out unposted loads.
  if (isUnposted) {
    return 'unposted';
  }

  // Let to book if it's a preferred carrier.
  return load.carrier_access.can_book ? 'can_book' : 'can_send_request';
}

export function isPostingUnposted(posting: PostingDTO) {
  return (
    posting.load.unposted_at ||
    posting.carrier_load?.status === 'UNPOSTED' ||
    posting.carrier_load?.status === 'OFFER_ACCEPTED' ||
    posting.carrier_load?.status === 'COUNTER_OFFER_SENT' ||
    posting.carrier_load?.status === 'OFFER_SENT'
  );
}

export function formatPostingDistance(
  address: string,
  distance?: PostingDistanceDTO | null,
) {
  const { distance_miles, distance_from, direction } = distance || {};

  if (
    !distance_miles ||
    !distance_from ||
    !direction ||
    address === distance_from
  ) {
    return null;
  }

  return joinStrings(
    ' ',
    `${distance_miles} mi`,
    direction,
    `of ${distance_from}`,
  );
}
